import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { FacilityRejectionReasonEnum } from '../../../api';
import { generateZEnumFromEnum } from '../../../utils/forms';
import { FormSelectField } from '../FormSelectField';

interface DenyClearanceDialogProps {
  show: boolean;
  closeDialog: (
    result: {
      rejection_reason: FacilityRejectionReasonEnum;
      rejection_details: string;
    } | null
  ) => void;
}

const validationSchema = z.object({
  rejection_reason: generateZEnumFromEnum(window.FACILITY_REJECTION_REASONS),
  rejection_details: z.string().optional(),
});

export const DenyPresentationDialog = ({ show, closeDialog }: DenyClearanceDialogProps) => {
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    rejection_reason?: boolean;
    rejection_details?: boolean;
  }>({});

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const reason = useWatch({ control, name: 'rejection_reason' });

  const handleSubmitClick = () => {
    clearErrors();
    setShowServerFieldErrors({});
  };

  const handleClose = () => {
    const defaultReason = Object.keys(window.REJECTION_REASONS)[0];

    reset({
      rejection_reason: defaultReason,
      details: '',
    });
    closeDialog(null);
  };

  const onSubmit = async (data) => {
    closeDialog(data);

    const defaultReason = Object.keys(window.FACILITY_REJECTION_REASONS)[0];
    reset({
      rejection_reason: defaultReason,
      details: '',
    });
  };

  return (
    <Modal show={show} onHide={() => closeDialog(null)}>
      <Modal.Header closeButton>
        <Modal.Title>Decline Provider’s Presentation</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body style={{ maxHeight: '60vh' }}>
          <FormSelectField
            name="rejection_reason"
            label="Please provide a reason for the denial:"
            options={window.FACILITY_REJECTION_REASONS}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
          />
          {reason === FacilityRejectionReasonEnum.OTHER && (
            <Form.Group>
              <Form.Control
                type="text"
                as="textarea"
                placeholder="Enter an explanation"
                {...register('rejection_details')}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleClose}>
            Back
          </Button>
          <Button variant="danger" type="submit" onClick={() => handleSubmitClick()}>
            Decline
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
